/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from 'types';

import { MonthDay } from '@components/payment-collection/constants';
import { ErrorCode } from '@constants/errorCodes';
import { FEATURE_FLAGS } from '@constants/feature';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  BranchByIdInitialState,
  BranchByIdType
} from '@features/branch-management/constants';
import { useLazyGetBranchByIdQuery } from '@features/branch-management/queries';
import { updateGroupInfo } from '@features/group-management/slices/groupSlice';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { setCurrencyConfig } from '@layouts/main-header/userSlice';
import {
  FeeCategoryDataType,
  FormDataType,
  TermsList,
  UpdateGroupRequest
} from '@types';
import { formatISODate } from '@utils/date';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { deepEqual } from '@utils/validations';
import { ValidationError } from 'types/requests';

import { PaymentCollectionType } from '../constants';
import {
  formatFeeConfiguration,
  formatGroupData,
  getBulkPaymentDiscountDetails,
  getBulkPaymentDiscountRowErrors,
  getFeeCategoryData,
  getGroupFeeCollectionMethodType,
  getTermDetails,
  groupFormDataDefaultValue,
  isAddNewGroupSaveDisabled,
  isBulkPaymentDiscountIncomplete
} from '../helper';
import {
  areCustomTermDueDatesAscending,
  CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
  isDateBefore
} from '../helpers/customTerm';
import { getUpdateGroupAnalyticParams } from '../pages/v1/group-info/helper';
import {
  useFetchMembersEligibleForUpdateMutation,
  useImageUploadMutation,
  useLazyGetGroupByIdQuery,
  useLazyGetGroupMembersQuery,
  useUpdateGroupMutation
} from '../queries/GroupQuery';
import { CustomMemberUpdate, UpdateEligibleMembersResponse } from '../types';

const useEditGroupForm = () => {
  const { groupId } = useParams();
  const branchId = getLocalStorageItem(STORAGE_KEYS.BRANCH_ID);
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    orgConfig,
    rolesAndPermissions,
    currentSubscription,
    orgFeatureFlags
  } = useSelector((state: any) => state.user);

  const isSyncGroupUpdateEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.SYNC_GROUP_UPDATE_TO_MEMBERS
  );

  const hasGroupAttendanceEditAccess =
    checkPermission([UserPermissionTypes.GROUP_ATTENDANCE_EDIT]) &&
    !!orgConfig?.is_attendance_feature_enabled;

  const [formData, setFormData] = useState<FormDataType>(
    groupFormDataDefaultValue
  );
  const [initialFormData, setInitialFormData] = useState<FormDataType>(
    groupFormDataDefaultValue
  );
  const [isFeeCategoryEnabled, setIsFeeCategoryEnabled] =
    useState<boolean>(false);
  const [feeCategories, setFeeCategories] = useState<FeeCategoryDataType[]>([]);
  const [initialFeeCategories, setInitialFeeCategories] = useState<
    FeeCategoryDataType[]
  >([]);
  const [branchDetails, setBranchDetails] = useState<BranchByIdType>(
    BranchByIdInitialState
  );
  const [customTermDueDateError, setCustomTermDueDateError] =
    useState<ValidationError>({});
  const [feeDetailsError, setFeeDetailsError] = useState<ValidationError>({});
  const [bulkPaymentDiscountRowErrors, setBulkPaymentDiscountRowErrors] =
    useState<string[]>([]);
  const [showAutoDebitModal, setShowAutoDebitModal] = useState<boolean>(false);
  const [showAmountChangeModal, setShowAmountChangeModal] =
    useState<boolean>(false);
  const [showReviewMembersModal, setShowReviewMembersModal] =
    useState<boolean>(false);
  const [eligibleMembersData, setEligibleMembersData] =
    useState<UpdateEligibleMembersResponse | null>(null);
  const [isPaymentLinkSentForGroup, setIsPaymentLinkSentForGroup] =
    useState<boolean>(false);
  const [showDueDateConsent, setShowDueDateConsent] = useState<boolean>(false);

  const [
    getGroupById,
    {
      data: groupData,
      isLoading: getGroupDataLoading,
      isFetching: getGroupDataFetching,
      isSuccess: getGroupDataSuccess,
      isUninitialized
    }
  ] = useLazyGetGroupByIdQuery();
  const [getAllMembers, { data: membersData }] = useLazyGetGroupMembersQuery();
  const [getBranchById] = useLazyGetBranchByIdQuery();
  const [uploadImage, { isLoading: uploadImageLoading }] =
    useImageUploadMutation();
  const [updateGroup, { isLoading: updateGroupLoading }] =
    useUpdateGroupMutation();
  const [fetchMembersEligibleForUpdate, { isLoading: isValidatingMembers }] =
    useFetchMembersEligibleForUpdateMutation();

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const isPageLoading =
    getGroupDataLoading ||
    getGroupDataFetching ||
    isUninitialized ||
    uploadImageLoading ||
    updateGroupLoading;

  const isAttendanceBasedFee = !!formData.isAttendanceBasedFeeEnabled;

  const sanitizeFormData = (data: FormDataType) => {
    const { groupPhoto, ...rest } = data;
    return {
      ...rest,
      groupPhoto: groupPhoto
        ? { ...groupPhoto, file: undefined, preview: undefined }
        : groupPhoto,
      termsList: data.termsList
        ? data.termsList.map((term) => ({
            ...term,
            feeCategoryData: term.feeCategoryData ?? []
          }))
        : data.termsList
    };
  };

  const hasChanges = !deepEqual(
    sanitizeFormData(formData),
    sanitizeFormData(initialFormData)
  );

  const hasTermDataChanged = (
    termsList: TermsList[],
    initialTermList: TermsList[]
  ): boolean => {
    if (termsList.length !== initialTermList.length) return true;
    return termsList.some((term, index) => {
      const initialTerm = initialTermList[index];
      return (
        term.dueAmount !== initialTerm.dueAmount ||
        term.term_label !== initialTerm.term_label ||
        new Date(new Date(term.dueDate).setHours(0, 0, 0, 0)).getTime() !==
          new Date(new Date(initialTerm.dueDate).setHours(0, 0, 0, 0)).getTime()
      );
    });
  };

  const hasChangesInCustomTerm =
    formData.isCustomTerm &&
    formData.termsList &&
    hasTermDataChanged(formData.termsList, initialFormData.termsList || []);

  const getIsDateInPast = (row: TermsList) => {
    const isDueDatePast =
      new Date(new Date(row.dueDate).setHours(0, 0, 0, 0)) <
      new Date(new Date().setHours(0, 0, 0, 0));
    return isDueDatePast;
  };

  const checkIsTermEdited = (row: TermsList) => {
    const term = initialFormData.termsList?.find((t) => t.id === row.id);
    if (!term) return false;
    return (
      term.dueAmount !== row.dueAmount ||
      term.term_label !== row.term_label ||
      new Date(new Date(term.dueDate).setHours(0, 0, 0, 0)).getTime() !==
        new Date(new Date(row.dueDate).setHours(0, 0, 0, 0)).getTime()
    );
  };

  const getApiParams = () => ({
    organizationId: getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID),
    branchId: getLocalStorageItem(STORAGE_KEYS.BRANCH_ID),
    groupId: groupId || ''
  });

  const getIntervalValue = (paymentCollection: PaymentCollectionType | '') => {
    if (
      paymentCollection === '' ||
      paymentCollection === PaymentCollectionType.BY_TERM
    )
      return PaymentCollectionType.MONTHLY;
    return paymentCollection;
  };

  const transformData = () =>
    membersData?.data?.map(
      (member: { group_member_id: number; name: string }) => ({
        group_member_id: member.group_member_id,
        name: member.name
      })
    ) || [];

  const onInputChange = (field: string, value: any) => {
    setFormData((prev: FormDataType) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAttendanceSwitchClick = (status: boolean) => {
    onInputChange('isAttandanceTrackingEnabled', status);
    sendMixpanelEvent(Event.ATTENDANCE_TRACKING_ENABLED, {
      status,
      page: 'Edit group'
    });
    if (status === false) {
      onInputChange('isSendAttendanceAlertEnabled', status);
      onInputChange('workingDays', []);
    }
  };

  const isOnceDueDateGroup =
    formData.paymentCollectionType === PaymentCollectionType.ONCE &&
    !formData.isImmediatePayment &&
    initialFormData.paymentCollectionType === PaymentCollectionType.ONCE;

  const hasRelevantChanges = (): boolean => {
    if (formData.amount !== initialFormData.amount) return true;
    if (hasChangesInCustomTerm) return true;
    if (!deepEqual(feeCategories, initialFeeCategories)) return true;

    if (isOnceDueDateGroup) return false;

    if (
      formData.paymentCollectionType !== initialFormData.paymentCollectionType
    )
      return true;
    if (formData.intervalFrequency !== initialFormData.intervalFrequency)
      return true;
    if (formData.startDate !== initialFormData.startDate) return true;
    if (formData.endDate !== initialFormData.endDate) return true;
    if (formData.activationDate !== initialFormData.activationDate) return true;
    if (formData.sessionsPerCycle !== initialFormData.sessionsPerCycle)
      return true;
    return false;
  };

  const buildUpdateGroupPayload = (
    group: FormDataType,
    customMemberUpdates?: CustomMemberUpdate[]
  ): UpdateGroupRequest => ({
    amount: group.amount,
    name: group.groupName.trim(),
    skip_current_interval:
      group.dueDateConsent === 'next' ||
      customMemberUpdates?.some(
        (update) => update.skip_current_interval && update.default_member
      ),
    is_split_payment: group.paymentInstallment,
    payment_schedule: {
      start_date: group?.startDate
        ? formatISODate({
            date: group.startDate,
            separator: '-',
            format: 'yyyy-mm-dd'
          })
        : null,
      end_date: group.endDate
        ? formatISODate({
            date: group.endDate,
            separator: '-',
            format: 'yyyy-mm-dd'
          })
        : null,
      interval: getIntervalValue(group.paymentCollectionType),
      interval_frequency: Number(group.intervalFrequency),
      ...(getIntervalValue(group.paymentCollectionType) ===
        PaymentCollectionType.MONTHLY && {
        monthly_config: {
          day_option:
            // eslint-disable-next-line no-nested-ternary
            group.activationDate === '1'
              ? MonthDay.FIRST
              : group.activationDate === MonthDay.FIRST ||
                group.activationDate === MonthDay.LAST
              ? group.activationDate
              : 'custom',
          custom_day: Number(group.activationDate)
        }
      }),
      ...(getIntervalValue(group.paymentCollectionType) ===
        PaymentCollectionType.WEEKLY && {
        weekly_config: {
          day_of_week: group.activationDate
        }
      }),
      ...(getIntervalValue(group.paymentCollectionType) ===
        PaymentCollectionType.ONCE &&
        !group.isImmediatePayment && {
          interval_execution_type: 'due_date',
          custom_date: group.activationDate
        }),
      ...(group.isCustomTerm &&
        formData.termsList && {
          term_details: getTermDetails(formData.termsList, isFeeCategoryEnabled)
        }),
      ...(getIntervalValue(group.paymentCollectionType) ===
        PaymentCollectionType.SESSION_BASED &&
        group.sessionsPerCycle && {
          sessions_per_cycle: group.sessionsPerCycle
        }),
      ...(group.isCustomTerm && {
        bulk_payment_discount_details: getBulkPaymentDiscountDetails(group)
      })
    },
    ...(!!orgConfig?.is_attendance_feature_enabled && {
      attendance_config: {
        is_attendance_tracking_enabled: group.isAttandanceTrackingEnabled,
        is_attendance_based_fee_enabled: !!group.isAttendanceBasedFee,
        ...(group.isAttandanceTrackingEnabled && {
          is_send_attendance_alert_enabled: group.isSendAttendanceAlertEnabled
        }),
        working_days: group?.workingDays
      }
    }),
    group_fee_configuration: getFeeCategoryData(feeCategories),
    is_fee_configured: isFeeCategoryEnabled,
    ...(groupData?.amount === null && {
      members: transformData()
    }),
    ...(customMemberUpdates && {
      member_updates: customMemberUpdates.map((update) => ({
        member_id: update.member_id,
        skip_current_interval: update.skip_current_interval
      }))
    })
  });

  const handleUpdateGroup = async (
    group: FormDataType,
    customMemberUpdates?: CustomMemberUpdate[]
  ) => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
      return;
    }

    if (hasChangesInCustomTerm && formData.isCustomTerm && formData.termsList) {
      const pastDueTerm = formData.termsList.find(
        (term) =>
          checkIsTermEdited(term) &&
          getIsDateInPast(term) &&
          Number(term.dueAmount) !== 0 &&
          term.isPaymentLinkSent === false
      );
      if (pastDueTerm) {
        setBulkPaymentDiscountRowErrors([]);
        setCustomTermDueDateError({
          dueDate: "Due date can't be in the past",
          dueDateRowId: pastDueTerm.id
        });
        return;
      }
    }

    if (
      formData.isCustomTerm &&
      formData.termsList &&
      !areCustomTermDueDatesAscending(formData.termsList)
    ) {
      const violatingTerm = formData.termsList.find(
        (term, i) =>
          i > 0 &&
          isDateBefore(term.dueDate, formData.termsList![i - 1].dueDate, true)
      );
      setBulkPaymentDiscountRowErrors([]);
      setCustomTermDueDateError({
        dueDate: CUSTOM_TERM_DUE_DATE_ORDER_ERROR,
        dueDateRowId: violatingTerm?.id || ''
      });
      return;
    }

    if (isBulkPaymentDiscountIncomplete(formData)) {
      setCustomTermDueDateError({});
      setBulkPaymentDiscountRowErrors(
        getBulkPaymentDiscountRowErrors(formData)
      );
      return;
    }

    const updatedGroupDetails = buildUpdateGroupPayload(
      group,
      customMemberUpdates
    );

    const payload = {
      params: getApiParams(),
      body: updatedGroupDetails
    };
    const res = await updateGroup(payload);
    if ('data' in res) {
      createSuccessAlert('Group updated successfully');
      navigate(`${GROUPS.HOME}/${groupId}`);
      const hasCustomMembersUpdated =
        !!customMemberUpdates &&
        eligibleMembersData?.eligible_members.some(
          (m) =>
            !m.is_default_member &&
            customMemberUpdates.some((u) => u.member_id === m.member_id)
        );
      sendMixpanelEvent(
        isFeeCategoryEnabled
          ? Event.GROUP_WITH_FEE_CATEGORY_EDITED
          : Event.GROUP_UPDATION_SUCCESS,
        {
          ...getUpdateGroupAnalyticParams(
            group,
            isFeeCategoryEnabled,
            feeCategories
          ),
          group_edit_review_modal: customMemberUpdates ? 'Yes' : 'No',
          custom_members_updated: hasCustomMembersUpdated ? 'Yes' : 'No'
        }
      );
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error.data?.errors?.[0]?.message ||
          error.data?.message ||
          'Error while updating the group'
      );
      if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
        if (
          rolesAndPermissions.permissions.includes(
            UserPermissionTypes.SUBSCRIPTION_VIEW
          )
        ) {
          navigate(SUBSCRIPTIONS.PLAN_DETAILS);
        } else {
          navigate(GROUPS.HOME);
        }
      }
      sendMixpanelEvent(
        isFeeCategoryEnabled
          ? Event.ERROR_IN_EDITING_GROUP_WITH_FEE_CATEGORY
          : Event.GROUP_UPDATION_ERROR,
        {
          ...getUpdateGroupAnalyticParams(
            group,
            isFeeCategoryEnabled,
            feeCategories
          ),
          error: error.data?.errorCode
        }
      );
    }
  };

  const isAutoDebitInValid = (group: FormDataType): boolean => {
    const autoDebitEnabledCount =
      groupData?.auto_debit_enabled_default_member_count || 0;
    const mandateMaxAmount = groupData?.group_member_mandate_max_amount || 0;
    const enteredAmount = parseFloat(group.amount) || 0;
    const originalAmount = parseFloat(initialFormData.amount) || 0;

    const hasAmountChanged = enteredAmount !== originalAmount;
    const autoDebitConditionsMet =
      autoDebitEnabledCount > 0 && mandateMaxAmount < enteredAmount;

    return hasAmountChanged && autoDebitConditionsMet;
  };

  const processGroupUpdate = async (
    group: FormDataType,
    customMemberUpdates?: CustomMemberUpdate[]
  ) => {
    if (group.groupPhoto?.file) {
      const imageData = new FormData();
      imageData.append('image', group.groupPhoto.file);
      imageData.append('type', 'group');
      imageData.append('id', groupId || '');
      const res = await uploadImage(imageData);
      if ('data' in res) {
        handleUpdateGroup(group, customMemberUpdates);
        sendMixpanelEvent(Event.GROUP_IMAGE_UPLOAD_SUCCESS, {
          group_name: group.groupName,
          flow: 'Group updation',
          group_type: getGroupFeeCollectionMethodType(
            group?.isAttendanceBasedFeeEnabled || false
          )
        });
      } else if ('error' in res) {
        const error = res.error as ApiError;
        createErrorAlert(
          error.data?.message
            ? capitalizeFirstLetter(error.data?.message)
            : 'Error while uploading the image'
        );
        sendMixpanelEvent(Event.GROUP_IMAGE_UPLOAD_ERROR, {
          group_name: group.groupName,
          error: error.data?.errorCode,
          flow: 'Group updation',
          group_type: getGroupFeeCollectionMethodType(
            group?.isAttendanceBasedFeeEnabled || false
          )
        });
      }
    } else {
      handleUpdateGroup(group, customMemberUpdates);
    }
  };

  const triggerMemberReviewOrUpdate = async (group: FormDataType) => {
    if (!isSyncGroupUpdateEnabled) {
      if (isPaymentLinkSentForGroup) {
        setShowDueDateConsent(true);
        return;
      }
      processGroupUpdate(group);
      return;
    }

    const isOnceImmediate =
      group.paymentCollectionType === PaymentCollectionType.ONCE &&
      group.isImmediatePayment;
    if (isOnceImmediate || !hasRelevantChanges()) {
      processGroupUpdate(group);
      return;
    }
    const res = await fetchMembersEligibleForUpdate({
      params: getApiParams(),
      body: buildUpdateGroupPayload(group)
    });

    if ('data' in res) {
      const { data } = res;
      if (
        data &&
        (data.eligible_members?.length || data.ineligible_members?.length)
      ) {
        setEligibleMembersData(data);
        setShowReviewMembersModal(true);
      } else if (isPaymentLinkSentForGroup) {
        setShowDueDateConsent(true);
      } else {
        processGroupUpdate(group);
      }
    } else {
      processGroupUpdate(group);
    }
  };

  const onConfirmReviewMembers = (
    customMemberUpdates: CustomMemberUpdate[]
  ) => {
    setShowReviewMembersModal(false);
    processGroupUpdate(formData, customMemberUpdates);
  };

  const onDueDateConsentConfirm = (consent: string) => {
    setShowDueDateConsent(false);
    const updatedFormData = {
      ...formData,
      dueDateConsent: consent
    } as FormDataType;
    processGroupUpdate(updatedFormData);
  };

  const onHandleAutoDebit = () => {
    setShowAutoDebitModal(false);
    triggerMemberReviewOrUpdate(formData);
  };

  const onHandleAmountChange = () => {
    setShowAmountChangeModal(false);
    triggerMemberReviewOrUpdate(formData);
  };

  const onSaveEditGroup = async () => {
    if (isAutoDebitInValid(formData)) {
      setShowAutoDebitModal(true);
      return;
    }

    if (isAttendanceBasedFee) {
      if (parseFloat(formData.amount) !== parseFloat(initialFormData.amount)) {
        setShowAmountChangeModal(true);
        return;
      }
    }
    triggerMemberReviewOrUpdate(formData);
  };

  const isSaveDisabled =
    !hasChanges ||
    isAddNewGroupSaveDisabled(
      formData,
      groupMemberMaxAmount,
      groupMemberMinAmount,
      feeCategories,
      isFeeCategoryEnabled
    ) ||
    !!customTermDueDateError.dueDate ||
    !!feeDetailsError.endDate ||
    bulkPaymentDiscountRowErrors.some(Boolean);

  const handleGetGroupById = async () => {
    const queryObject = {
      include_payment_stats: true,
      include_group_stats: true,
      include_payment_details: true,
      include_fee_categories: true
    };
    const queryString = buildQueryString(queryObject);
    const payload = {
      params: getApiParams(),
      queryString
    };
    await getGroupById(payload);
  };

  const handleGetAllMembers = async () => {
    const queryObject = {
      include_payment_stats: true,
      include_group_stats: true
    };
    const queryString = buildQueryString(queryObject);
    const payload = {
      params: getApiParams(),
      queryString
    };
    await getAllMembers(payload);
  };

  const handleGetBranchDetails = async () => {
    const res = await getBranchById({
      params: { organizationId, branchId: branchId || '' },
      queryString: '?include_fee_categories=true&include_payment_stats=false'
    });
    if ('data' in res) {
      setBranchDetails(res.data);
      if (res.data?.currency) {
        dispatch(setCurrencyConfig(res.data.currency));
      }
    } else if ('error' in res) {
      const apiError = res.error as ApiError;
      createErrorAlert(
        apiError.data?.message || 'Error in fetching branch details'
      );
    }
  };

  useEffect(() => {
    handleGetGroupById();
    handleGetBranchDetails();
  }, []);

  useEffect(() => {
    if (getGroupDataSuccess && groupData) {
      const groupFormData = formatGroupData(groupData);
      setFormData(groupFormData);
      setInitialFormData(groupFormData);
      setIsFeeCategoryEnabled(!!groupData.is_fee_configured);

      const feeConfiguration = formatFeeConfiguration(
        groupData.group_fee_amount_configuration || []
      );
      setFeeCategories(feeConfiguration);
      setInitialFeeCategories(feeConfiguration);

      const {
        frequency,
        interval_frequency: interval,
        name,
        amount,
        next_due_at: dueDate
      } = groupData;
      dispatch(
        updateGroupInfo({
          frequency,
          interval,
          name,
          groupAmount: amount,
          dueDate,
          isActive: groupData.is_active,
          startDate: groupData?.start_date,
          isImmediatePayment:
            groupData?.interval_execution_type === 'immediate',
          isCustomTerm:
            groupData?.payment_schedule?.interval ===
            PaymentCollectionType.CUSTOM_TERM,
          monthlyConfig: groupData?.payment_schedule?.monthly_config,
          weeklyConfig: groupData?.payment_schedule?.weekly_config,
          termsList: groupData?.payment_schedule?.term_details
        })
      );
    }
  }, [getGroupDataSuccess, groupData]);

  useEffect(() => {
    if (groupData?.amount === null) {
      handleGetAllMembers();
    }
  }, [groupData?.amount]);

  return {
    formData,
    initialFormData,
    onInputChange,
    memberList: [],
    isFeeCategoryEnabled,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    feeDetailsError,
    setFeeDetailsError,
    bulkPaymentDiscountRowErrors,
    setBulkPaymentDiscountRowErrors,
    isSaveDisabled: isSaveDisabled as boolean,
    isLoading: uploadImageLoading || updateGroupLoading,
    isPageLoading,
    onSaveEditGroup,
    hasGroupAttendanceEditAccess,
    handleAttendanceSwitchClick,
    groupId: groupId || '',
    isAttendanceBasedFee,
    hasChanges,
    showAutoDebitModal,
    setShowAutoDebitModal,
    onHandleAutoDebit,
    groupData,
    showAmountChangeModal,
    setShowAmountChangeModal,
    onHandleAmountChange,
    showReviewMembersModal,
    setShowReviewMembersModal,
    eligibleMembersData,
    onConfirmReviewMembers,
    isValidatingMembers,
    isPaymentLinkSentForGroup,
    setIsPaymentLinkSentForGroup,
    showDueDateConsent,
    setShowDueDateConsent,
    onDueDateConsentConfirm
  };
};

export default useEditGroupForm;
